import React, { useState, useCallback } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import Box from '@material-ui/core/Box'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import config from '../../config'

function getAuthToken() {
	try {
		return localStorage.getItem('token') || ''
	} catch { return '' }
}

function confidenceColor(c) {
	if (c === 'high') return '#2e7d32'
	if (c === 'medium') return '#e65100'
	return '#c62828'
}

export default function BulkEnrichDialog({ open, onClose, products }) {
	const [results, setResults] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)

	const handleBulkEnrich = useCallback(async () => {
		if (!products || products.length === 0) return
		setLoading(true)
		setError(null)
		setResults(null)

		const productIds = products
			.filter((p) => p.isSmart == null)
			.map((p) => p.productId)
			.slice(0, 50)

		if (productIds.length === 0) {
			setError('No products with unknown IsSmart status in current view.')
			setLoading(false)
			return
		}

		try {
			const resp = await fetch(
				`${config.API_URL}/api/admin/products/bulk-enrich`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${getAuthToken()}`,
					},
					body: JSON.stringify({ productIds }),
				}
			)
			const data = await resp.json()
			if (Array.isArray(data)) {
				setResults(data)
			} else {
				setError(data?.message || 'Unexpected response.')
			}
		} catch (err) {
			setError(err.message || 'Network error.')
		} finally {
			setLoading(false)
		}
	}, [products])

	const unknownCount = products
		? products.filter((p) => p.isSmart == null).length
		: 0

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>Bulk AI Enrichment</DialogTitle>
			<DialogContent dividers>
				<Typography variant="body2" gutterBottom>
					This will send products with unknown Smart status to OpenAI for
					classification. Up to 50 products per batch.
				</Typography>
				<Typography variant="body2" color="textSecondary" gutterBottom>
					{unknownCount} product(s) with unknown IsSmart in current view.
				</Typography>

				<Box mt={1} mb={2}>
					<Button
						variant="contained"
						color="secondary"
						onClick={handleBulkEnrich}
						disabled={loading || unknownCount === 0}
						startIcon={loading ? <CircularProgress size={16} /> : null}
					>
						{loading
							? 'Enriching...'
							: `Enrich ${unknownCount} product(s)`}
					</Button>
				</Box>

				{error && (
					<Typography variant="body2" color="error" gutterBottom>
						{error}
					</Typography>
				)}

				{results && results.length > 0 && (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Brand / Model</TableCell>
								<TableCell>Smart</TableCell>
								<TableCell>Battery Type</TableCell>
								<TableCell>Inverter Type</TableCell>
								<TableCell>Confidence</TableCell>
								<TableCell>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{results.map((r) => (
								<TableRow key={r.productId}>
									<TableCell>
										{r.brand} {r.model}
									</TableCell>
									<TableCell>
										{r.success
											? r.suggestedIsSmart === true
												? 'Yes'
												: r.suggestedIsSmart === false
													? 'No'
													: '—'
											: '—'}
									</TableCell>
									<TableCell>
										{r.suggestedBatteryComponentType || '—'}
									</TableCell>
									<TableCell>
										{r.suggestedInverterType || '—'}
									</TableCell>
									<TableCell>
										{r.confidence ? (
											<Chip
												size="small"
												label={r.confidence}
												style={{
													backgroundColor: confidenceColor(r.confidence),
													color: '#fff',
													fontWeight: 500,
												}}
											/>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell>
										{r.success ? (
											<CheckCircleIcon
												style={{ color: '#2e7d32', fontSize: 18 }}
											/>
										) : (
											<ErrorIcon
												style={{ color: '#c62828', fontSize: 18 }}
												titleAccess={r.error}
											/>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="default" size="small">
					Close
				</Button>
			</DialogActions>
		</Dialog>
	)
}
